import { z } from 'zod';
import { Id, IsoDateTime } from './common.js';
import { ChannelType, MessageType } from './channels.js';

export const AiStatus = z.enum(['active', 'paused_auto', 'paused_manual', 'disabled']);
export type AiStatus = z.infer<typeof AiStatus>;

export const MessageDirection = z.enum(['inbound', 'outbound']);
export const MessageAuthor = z.enum(['customer', 'ai', 'merchant', 'system']);

export const Message = z.object({
  message_id: Id,
  conversation_id: Id,
  merchant_id: Id,
  channel_id: Id,
  customer_id: Id,
  direction: MessageDirection,
  author: MessageAuthor,
  type: MessageType,
  text: z.string().nullable(),
  media: z
    .object({
      media_id: z.string(),
      kind: z.string(),
      mime_type: z.string(),
      file_size: z.number().int().nullable(),
      download_url: z.string().nullable(),
      duration_ms: z.number().int().nullable().optional(),
      filename: z.string().nullable().optional(),
    })
    .nullable(),
  external_message_id: z.string().nullable(),
  status: z.enum(['queued', 'sent', 'delivered', 'read', 'failed']).default('queued'),
  error: z.string().nullable().optional(),
  created_at: IsoDateTime,
});
export type Message = z.infer<typeof Message>;

export const Conversation = z.object({
  conversation_id: Id,
  merchant_id: Id,
  channel_id: Id,
  channel_type: ChannelType,
  customer_id: Id,
  external_user_id: z.string(),
  ai_status: AiStatus,
  ai_status_until: IsoDateTime.nullable(),
  assigned_to_user_id: Id.nullable(),
  last_inbound_at: IsoDateTime.nullable(),
  last_outbound_at: IsoDateTime.nullable(),
  unread_count: z.number().int().nonnegative().default(0),
  tags: z.array(z.string()).default([]),
  is_resolved: z.boolean().default(false),
  created_at: IsoDateTime,
});
export type Conversation = z.infer<typeof Conversation>;

export const EscalationReason = z.enum([
  'complaint',
  'refund_request',
  'out_of_scope',
  'high_value_order',
  'customer_request',
  'stuck',
  'complex_query',
  'sensitive_topic',
  'tool_failure',
]);
export type EscalationReason = z.infer<typeof EscalationReason>;

export const Escalation = z.object({
  escalation_id: Id,
  merchant_id: Id,
  conversation_id: Id,
  reason: EscalationReason,
  summary: z.string(),
  resolved_at: IsoDateTime.nullable(),
  created_at: IsoDateTime,
});
